import type { FC } from 'react'
import { useState } from 'react'

import { Popup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { ONE_TIME_ORDER_INTERVAL } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import CheckboxIcon from '@/components/CheckboxIcon'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { useIsAdmin } from '@/hooks/access'
import { commonEcomPolicyResources } from '@/resources/ecom-policy'
import { commonQueries } from '@/resources/queries'
import { numberFormat } from '@/util/formatters/numbers'

import queries from '../queries'
import adminResources from '../resources'

import type { PolicyId } from './types'

const PolicyFilter: FC<{
  currentEcomPolicyFilter: PolicyId[] | null
  currentTelePolicyFilter: PolicyId[] | null
  onClose: () => void
  onApplyFilters: (filters: {
    ecomPolicyIds: PolicyId[] | null
    telePolicyIds: PolicyId[] | null
  }) => void
}> = ({ currentEcomPolicyFilter, currentTelePolicyFilter, onClose, onApplyFilters }) => {
  const translate = useTranslation()
  const isAdmin = useIsAdmin()

  const [locallySelectedEcomPolicyFilterIds, setLocallySelectedEcomPolicyFilterIds] = useState<
    PolicyId[] | null
  >(currentEcomPolicyFilter)
  const [locallySelectedTelePolicyFilterIds, setLocallySelectedTelePolicyFilterIds] = useState<
    PolicyId[] | null
  >(currentTelePolicyFilter)

  const { data: availablePolicies } = useQuery(
    commonQueries.policyList(),
    commonEcomPolicyResources.getList
  )
  const ecomPolicies = availablePolicies?.filter(policy => policy.isActive)
  const { data: telePolicies } = useQuery(
    queries.adminTelePoliciesList(),
    adminResources.getTelePolicyList,
    {
      enabled: isAdmin,
    }
  )

  const isNoTelePolicySelected = locallySelectedTelePolicyFilterIds?.[0] === 'no-policy'
  const isNoEcomPolicySelected = Boolean(locallySelectedEcomPolicyFilterIds?.includes('no-policy'))

  return (
    <Popup
      className="policy-filter-popup"
      ariaLabel={translate('Policy filter list')}
      isOpen={true}
      onClose={onClose}
    >
      <h2>
        <Translation id="Ecom policies" />
      </h2>
      <table className="policy-filter-table mb-5">
        <thead>
          <tr>
            <th />
            <th>
              <Translation id="Name" />
            </th>
            <th className="is-mobile-hidden text-right">
              <Translation id="Cover amount" />
            </th>
            <th colSpan={2} className="center" />
          </tr>
        </thead>
        <tbody>
          {ecomPolicies?.map(policy => {
            const isSelected = Boolean(
              locallySelectedEcomPolicyFilterIds?.find(
                selectedPolicyId => selectedPolicyId === policy.id
              )
            )
            return (
              <tr
                key={policy.id}
                className={`policy-subscription ${isSelected ? 'is-selected' : ''} `}
                onClick={() => {
                  const updatedList = isSelected
                    ? locallySelectedEcomPolicyFilterIds?.filter(
                        selectedPolicyId => selectedPolicyId !== policy.id
                      )
                    : [...(locallySelectedEcomPolicyFilterIds || []), policy.id]
                  setLocallySelectedEcomPolicyFilterIds(updatedList || null)
                }}
              >
                <CheckboxIcon isSelected={isSelected} />
                <td>
                  <div>{policy.name}</div>
                  <div>
                    <small>{policy.vendorName}</small>
                  </div>
                  <div>
                    <small>{policy.description}</small>
                  </div>
                </td>

                <td className="is-mobile-hidden text-right">
                  <div>
                    <FormattedNumberWithCurrency amount={policy.coverAmount || 0} />
                  </div>
                  <div>
                    {policy.orderInterval === ONE_TIME_ORDER_INTERVAL ? (
                      <small>{translate('One-time order')}</small>
                    ) : (
                      <small>
                        {numberFormat(policy.orderInterval)} {translate('mos')}
                      </small>
                    )}
                  </div>
                </td>
                <td className="is-mobile-hidden" />
              </tr>
            )
          })}
          <tr
            key="no-ecom-policy"
            className={`policy-subscription ${isNoEcomPolicySelected ? 'is-selected' : ''} `}
            onClick={() => {
              const updatedList = isNoTelePolicySelected
                ? locallySelectedEcomPolicyFilterIds?.filter(
                    selectedPolicyId => selectedPolicyId !== 'no-policy'
                  )
                : [...(locallySelectedEcomPolicyFilterIds || []), 'no-policy' as const]
              setLocallySelectedEcomPolicyFilterIds(updatedList || null)
            }}
          >
            <CheckboxIcon isSelected={isNoEcomPolicySelected} />
            <td>
              <div>
                <Translation id="No policy" />
              </div>
            </td>

            <td className="is-mobile-hidden" />
          </tr>
        </tbody>
      </table>

      <h2>
        <Translation id="Tele policies" />
      </h2>

      <table className="policy-filter-table">
        <thead>
          <tr>
            <th />
            <th>
              <Translation id="Name" />
            </th>
            <th className="is-mobile-hidden text-right">
              <Translation id="Template" />
            </th>
            <th colSpan={2} className="center" />
          </tr>
        </thead>
        <tbody>
          {telePolicies?.map(policy => {
            const isSelected = locallySelectedTelePolicyFilterIds?.[0] === policy.id
            return (
              <tr
                key={policy.id}
                className={`policy-subscription ${isSelected ? 'is-selected' : ''} `}
                onClick={() => {
                  const updatedId = isSelected ? null : policy.id
                  setLocallySelectedTelePolicyFilterIds(updatedId ? [updatedId] : null)
                }}
              >
                <CheckboxIcon isSelected={isSelected} />
                <td>
                  <div>{policy.name}</div>
                </td>
                <td>
                  {policy.telePolicyTemplate && (
                    <div>
                      <small>
                        <Translation id="Template:" /> <b>{policy.telePolicyTemplate.name}</b>
                      </small>
                    </div>
                  )}
                </td>

                <td className="is-mobile-hidden" />
              </tr>
            )
          })}
          <tr
            key="no-tele-policy"
            className={`policy-subscription ${isNoTelePolicySelected ? 'is-selected' : ''} `}
            onClick={() => {
              const updatedId = isNoTelePolicySelected ? null : 'no-policy'
              setLocallySelectedTelePolicyFilterIds(updatedId ? [updatedId] : null)
            }}
          >
            <CheckboxIcon isSelected={isNoTelePolicySelected} />
            <td>
              <div>
                <Translation id="No policy" />
              </div>
            </td>

            <td className="is-mobile-hidden" />
          </tr>
        </tbody>
      </table>

      <footer>
        <Button variant="tertiary" onClick={onClose}>
          <Translation id="Cancel" />
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            onApplyFilters({
              ecomPolicyIds: locallySelectedEcomPolicyFilterIds,
              telePolicyIds: locallySelectedTelePolicyFilterIds,
            })
            onClose()
          }}
        >
          <Translation id="Apply" />
        </Button>
      </footer>
    </Popup>
  )
}

export default PolicyFilter
